
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, Loader2, SendIcon } from "lucide-react";

interface ChatInputProps {
  onSubmit: (message: string) => Promise<void>;
  isLoading: boolean;
  remainingCredits: number;
  lastError: string | null;
  promptValue?: string; // For handling prompt content
  isAdmin?: boolean; // Added to check if user is admin
}

const ChatInput = ({ onSubmit, isLoading, remainingCredits, lastError, promptValue, isAdmin = false }: ChatInputProps) => {
  const [input, setInput] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Set the input value when promptValue changes
  useEffect(() => {
    if (promptValue) {
      setInput(promptValue);
    }
  }, [promptValue]);

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "100px"; // Increase base height
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = Math.min(200, scrollHeight) + "px"; // Increased max height
    }
  }, [input]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Don't allow submission if no input, loading, or out of credits (unless admin)
    if (!input.trim() || isLoading || (remainingCredits <= 0 && !isAdmin)) return;
    
    try {
      await onSubmit(input);
      setInput("");
      
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = "100px";
      }
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  return (
    <div className="border-t p-4">
      {lastError && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-2 rounded-md mb-3 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span>{lastError}</span>
        </div>
      )}
      
      {remainingCredits <= 0 && !isAdmin && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 rounded-md mb-3 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span>
            You've used all your credits for this month. Credits will refresh at the beginning of next month.
          </span>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={remainingCredits <= 0 && !isAdmin ? "Out of credits" : "Type a message..."}
          className="min-h-[100px] flex-1 resize-none" // Increased min-height
          disabled={isLoading || (remainingCredits <= 0 && !isAdmin)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
        />
        <div className="flex flex-col gap-2">
          <Button
            type="submit"
            disabled={isLoading || !input.trim() || (remainingCredits <= 0 && !isAdmin)}
            className="self-end"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <SendIcon className="h-4 w-4" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ChatInput;
